import { SxProps, Theme } from '@mui/material';

// Container styles - padding handled by Container component itself
export const containerStyles: SxProps<Theme> = {
  mt: 2,
  mb: 0
};

// Internal box wrapper - no horizontal padding since Container now handles alignment with AppBar
export const internalBoxStyles: SxProps<Theme> = {
  px: 0,  // No padding - Container provides alignment with AppBar
  display: 'flex',
  flexDirection: 'column',
  gap: 1  // Reduced from 2 (16px -> 8px)
};

// Status bar container styles - full width constrained by internal box
export const statusBarContainerStyles: SxProps<Theme> = {
  width: '100%',
  mb: { xs: 1, sm: 1, md: 3, lg: 3, xl: 3 }
};

// Content div - flexbox with legend on left/map on right (landscape) or map top/legend bottom (portrait)
export const getContentDivStyles = (isPortrait: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: isPortrait ? 'column' : 'row',
  gap: isPortrait ? 0 : 2,
  alignItems: isPortrait ? 'stretch' : 'center',
  width: '100%'
});

// Standalone legend container - regular positioning, only as wide as needed
export const standaloneLegendContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  padding: { xs: 1, sm: 1.5 },
  borderRadius: 1,
  border: '1px solid rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  width: { xs: '110px', sm: '150px', lg: '170px' },
  fontSize: { xs: '9px', sm: '12px', lg: '12px' },
  flexShrink: 0,
  alignSelf: 'center',  // Align to center
  marginTop: '-60px',  // Nudge slightly above center
  '@media (min-width: 1920px)': {
    width: '190px'
  },
  '@media (min-width: 2560px)': {
    width: '210px',
    transform: 'scale(1.1)'
  }
};

// Horizontal legend container - for portrait mode below map
export const horizontalLegendContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  padding: { xs: 1.5, sm: 2 },
  borderRadius: 1,
  border: '1px solid rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  width: 'auto',
  fontSize: { xs: '10px', sm: '12px' },
  alignItems: 'center',
  mt: 2,
  alignSelf: 'center'
};

// Horizontal legend title
export const horizontalLegendTitleStyles: SxProps<Theme> = {
  fontWeight: 'bold',
  mb: 1,
  fontSize: { xs: '11px', sm: '13px' },
  opacity: 0.9,
  textAlign: 'center'
};

// Horizontal legend status row (Current | Forecast)
export const horizontalLegendStatusRowStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  mb: 0.5
};

// Horizontal legend items container - wraps items horizontally
export const horizontalLegendItemsContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: { xs: 1.5, sm: 2 },
  justifyContent: 'center',
  alignItems: 'center'
};

// Loading div wrapper - centered content
export const loadingDivStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  width: '100%'
};

// Main layout container styles - simplified, centered (deprecated, kept for compatibility)
export const layoutContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  alignItems: 'center',
  mb: 3
};

// Map container styles
export const getMapContainerStyles = (isPortrait: boolean): SxProps<Theme> => ({
  width: isPortrait ? '100%' : {
    xs: '100%',
    sm: '450px',
    md: '530px',
    lg: '640px',
    xl: '750px'
  },
  aspectRatio: isPortrait ? '3 / 2' : '4 / 3',
  maxHeight: isPortrait ? {
    xs: '600px',
    sm: '620px',
    md: '680px',
    lg: '750px',
    xl: '800px'
  } : {
    xs: '500px',
    sm: '550px',
    md: '580px',
    lg: '650px',
    xl: '700px'
  },
  ...(isPortrait ? {
    '@media (min-width: 2000px)': {
      maxHeight: '1400px'
    },
    '@media (min-width: 3840px)': {
      maxHeight: '5500px'
    }
  } : {
    '@media (min-width: 2000px)': {
      maxHeight: '1000px'
    },
    '@media (min-width: 3840px)': {
      maxHeight: '1500px'
    }
  }),
  margin: '0 auto',
  // Map visual styling - change these to adjust map container appearance
  backgroundColor: 'background.default',  // Match theme background for seamless integration
  borderRadius: 0,                        // Remove rounded corners
  overflow: 'hidden',
  boxShadow: 'none',                      // Remove drop shadow
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flexGrow: 1
});

// Legend container styles
export const legendContainerStyles: SxProps<Theme> = {
  position: 'absolute',
  top: 16,
  left: 16,
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  padding: { xs: 1, sm: 1.5 },
  borderRadius: 1,
  border: '1px solid rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  width: { xs: '110px', sm: '150px', lg: '170px' },
  fontSize: { xs: '9px', sm: '12px', lg: '12px' },
  '@media (min-width: 1920px)': {
    width: '190px'
  },
  '@media (min-width: 2560px)': {
    width: '210px',
    transform: 'scale(1.1)'
  }
};

// Legend title styles
export const legendTitleStyles: SxProps<Theme> = {
  fontWeight: 'bold',
  mb: 0.5,
  fontSize: { xs: '10px', sm: '12px', lg: '14px' },
  '@media (min-width: 1920px)': { fontSize: '18px' },
  '@media (min-width: 2560px)': { fontSize: '20px' },
  opacity: 0.9,
  textAlign: 'center'
};

// Legend status container styles
export const legendStatusContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  mb: 1
};

// Legend icon container styles
export const legendIconContainerStyles: SxProps<Theme> = {
  width: { xs: 10, sm: 14, lg: 16, xl: 20 },
  height: { xs: 10, sm: 14, lg: 16, xl: 20 },
  mr: 0.8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

// Legend text styles
export const legendTextStyles: SxProps<Theme> = {
  fontSize: { xs: '9px', sm: '11px', lg: '12px' },
  '@media (min-width: 1920px)': { fontSize: '16px' },
  '@media (min-width: 2560px)': { fontSize: '18px' },
  opacity: 0.8,
  whiteSpace: 'nowrap'
};

// Legend color box styles
export const legendColorBoxStyles = (color: string): SxProps<Theme> => ({
  width: { xs: 10, sm: 14, lg: 16, xl: 20 },
  height: { xs: 10, sm: 14, lg: 16, xl: 20 },
  backgroundColor: color,
  mr: 0.8,
  borderRadius: '50%',
  border: '1px solid rgba(255, 255, 255, 0.3)'
});

// Legend items row styles
export const legendItemRowStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center'
};

// Legend items container styles
export const legendItemsContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1
};

// Tooltip styles
export const getTooltipStyles = (x: number, y: number): SxProps<Theme> => ({
  position: 'absolute',
  left: `${x}px`,
  top: `${y - 10}px`,
  transform: 'translate(-50%, -100%)',
  backgroundColor: 'rgba(0, 0, 0, 0.95)',
  color: '#fff',
  padding: '8px 12px',
  borderRadius: '4px',
  fontSize: '13px',
  pointerEvents: 'none',
  zIndex: 1000,
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  minWidth: '140px'
});

// Tooltip color box styles
export const tooltipColorBoxStyles = (color: string): SxProps<Theme> => ({
  width: 10,
  height: 10,
  backgroundColor: color,
  display: 'inline-block',
  mr: 0.5,
  borderRadius: '50%',
  border: '1px solid rgba(255, 255, 255, 0.5)'
});

// Sidebar container styles (40% width on desktop)
export const sidebarContainerStyles: SxProps<Theme> = {
  width: { xs: '100%', lg: '40%' },
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  height: { xs: 'auto', lg: '75vh' },
  maxHeight: { xs: 'none', lg: '75vh' },
  overflowY: { xs: 'visible', lg: 'auto' }
};

// Status box styles
export const statusBoxStyles = (color: string): SxProps<Theme> => ({
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
  borderRadius: 2,
  p: { xs: 2, sm: 3 },
  minHeight: { xs: 80, sm: 100 },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderLeft: `4px solid ${color}`,
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  mb: 2
});

// Status title styles
export const statusTitleStyles: SxProps<Theme> = {
  variant: 'h4',
  mb: 1,
  fontWeight: 'bold',
  textAlign: 'center',
  fontSize: { xs: '1.5rem', sm: '2rem' }
};

// Status message styles
export const statusMessageStyles: SxProps<Theme> = {
  variant: 'h6',
  textAlign: 'center',
  opacity: 0.9,
  fontSize: { xs: '0.9rem', sm: '1rem' }
};

// Airport Forecast section styles
export const airportForecastContainerStyles: SxProps<Theme> = {
  backgroundColor: '#111',
  borderRadius: 2,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
  display: 'flex',
  flexDirection: 'column'
};

export const airportForecastHeaderStyles: SxProps<Theme> = {
  px: 2,
  pt: 1,  // Reduced from 1.5 to 1 for tighter vertical spacing
  pb: 1,  // Reduced from 1.5 to 1 for tighter vertical spacing
  borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
  backgroundColor: 'rgba(0, 0, 0, 0.3)'
};

export const airportForecastTitleStyles: SxProps<Theme> = {
  mb: 0.5,
  color: '#4dd0e1',
  fontSize: '1.25rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  '@media (min-width: 1920px)': { fontSize: '1.75rem' },
  '@media (min-width: 2560px)': { fontSize: '2rem' }
};

export const airportForecastSubtitleStyles: SxProps<Theme> = {
  color: '#ccc',
  fontSize: '0.8rem',
  '@media (min-width: 1920px)': { fontSize: '1rem' },
  '@media (min-width: 2560px)': { fontSize: '1.15rem' }
};

// Dual timeline container styles
export const dualTimelineContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,  // Minimal gap between timelines
  p: 0.25,  // Minimal padding
  backgroundColor: 'rgba(255,255,255,0.02)'
};

// Timeline divider between slots
export const timelineDividerStyles: SxProps<Theme> = {
  width: '100%',
  height: '2px',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  mt: 0.25,  // Reduced from 0.5 to 0.25 for tighter vertical spacing
  mb: 0.5    // Reduced from 1 to 0.5 for tighter vertical spacing
};

// Individual timeline box styles
export const timelineBoxStyles: SxProps<Theme> = {
  overflow: 'visible',
  p: 0,  // Remove padding entirely
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  borderRadius: 1
};

// No airport selected styles
export const noAirportSelectedContainerStyles: SxProps<Theme> = {
  p: 2,
  textAlign: 'center'
};

export const noAirportSelectedTextStyles: SxProps<Theme> = {
  color: '#999',
  fontStyle: 'italic',
  fontSize: '0.875rem',
  '@media (min-width: 1920px)': { fontSize: '1.125rem' },
  '@media (min-width: 2560px)': { fontSize: '1.25rem' }
};

// Refresh bar styles
export const refreshBarContainerStyles: SxProps<Theme> = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'background.paper',
  borderTop: 1,
  borderColor: 'divider',
  zIndex: 1100
};

export const refreshBarContentStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: { xs: 36, sm: 40 },
  px: { xs: 2, sm: 3 },
  maxWidth: '100%',
  mx: 'auto'
};

export const refreshBarLeftSideStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  flex: '0 1 auto'
};

export const getRefreshStatusDotStyles = (freshnessColor: string): SxProps<Theme> => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: `${freshnessColor}.main`,
  flexShrink: 0
});

export const refreshBarTextStyles: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '0.75rem', sm: '0.875rem' },
  whiteSpace: 'nowrap'
};

export const refreshButtonStyles: SxProps<Theme> = {
  color: 'primary.main',
  '&:hover': {
    backgroundColor: 'action.hover'
  }
};

export const refreshIconStyles: SxProps<Theme> = {
  fontSize: 20
};

// View all timelines link styles
export const viewAllTimelineLinkStyles: SxProps<Theme> = {
  color: '#4dd0e1',
  cursor: 'pointer',
  fontSize: '0.75rem',
  '&:hover': { textDecoration: 'underline' }
};

// Container with refresh bar padding styles
export const getContainerWithRefreshBarStyles = (hasExistingData: boolean): SxProps<Theme> => ({
  ...containerStyles,
  // Reserve space for the fixed refresh bar at bottom
  pb: hasExistingData ? { xs: '48px', sm: '56px' } : 0
});

// Airport forecast header flex container styles (item 5)
export const airportForecastHeaderFlexStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: 0.25
};

// Feedback FAB styles - aligned with AppBar menu icons
export const feedbackFabStyles: SxProps<Theme> = {
  position: 'fixed',
  bottom: { xs: 50, sm: 58 },
  right: { xs: '12px', sm: '24px' },  // Match visual alignment with AppBar icons (1.5 * 8px, 3 * 8px)
  backgroundColor: '#4dd0e1',
  '&:hover': {
    backgroundColor: '#3db9c7'
  },
  zIndex: 1200,  // Higher z-index to ensure it's above refresh bar
  transform: 'scale(0.9)'  // Slightly smaller scale
};

// Feedback FAB icon styles
export const feedbackFabIconStyles: SxProps<Theme> = {
  fontSize: 22
};


// Drawer paper styles - fixed width on desktop, full width on mobile
export const drawerPaperStyles: SxProps<Theme> = {
  backgroundColor: 'background.default',
  borderLeft: 1,
  borderColor: 'divider',
  pt: 1,  // Reduced from 2 to 1 for tighter vertical spacing
  px: 2,
  width: {
    xs: '100%',     // Mobile: full width overlay
    sm: '90%',      // Small: 90% width overlay
    md: '50%'       // Desktop: 50% width
  },
  overflowY: 'auto'
};

// Drawer content styles
export const drawerContentStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  height: '100%',
  maxHeight: '100vh',
  overflowY: 'auto'
};

// Drawer toggle button styles - positioned in lower right, directly above feedback button
export const drawerToggleButtonStyles: SxProps<Theme> = {
  position: 'fixed',
  bottom: { xs: 110, sm: 118 },
  right: { xs: '12px', sm: '24px' },  // Match visual alignment with AppBar icons (1.5 * 8px, 3 * 8px)
  backgroundColor: '#4dd0e1',
  '&:hover': {
    backgroundColor: '#3db9c7'
  },
  zIndex: 1200,
  transform: 'scale(0.9)'
};

// Drawer toggle button icon styles
export const drawerToggleIconStyles: SxProps<Theme> = {
  fontSize: 22,
  color: '#000'
};

// Drawer close button container styles
export const drawerCloseButtonContainerStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  mb: 0.5  // Reduced from 1 to 0.5 for tighter vertical spacing
};

// Drawer close button styles
export const drawerCloseButtonStyles: SxProps<Theme> = {
  color: 'text.secondary',
  '&:hover': {
    backgroundColor: 'action.hover'
  }
};

// Drawer modal styles - positions the modal container below header
export const drawerModalStyles: SxProps<Theme> = {
  top: { xs: '56px', sm: '64px' },
  height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' }
};